import { useEffect, useRef } from "react";

/**
 * Signature pad (Phase 3) — a canvas the operator/finder draws on; emits a PNG data-URL. Host-local
 * for now (reused across the intake terms steps); promote to @fawkes/ui once the release wizard needs
 * it too. Mirrors the Jmix terms-step signature capture (`strayTermsSignature` is a base64 data-URL).
 */
export function SignaturePad({
  value,
  onChange,
  retryLabel,
  placeholder,
}: {
  value: string;
  onChange: (dataUrl: string) => void;
  retryLabel: string;
  placeholder: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);

  // size the pixel buffer to the displayed box once, and re-draw an existing signature
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    const rect = c.getBoundingClientRect();
    c.width = rect.width || 480;
    c.height = rect.height || 140;
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111827";
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, c.width, c.height);
      img.src = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function at(e: React.PointerEvent<HTMLCanvasElement>) {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }
  function down(e: React.PointerEvent<HTMLCanvasElement>) {
    drawing.current = true;
    last.current = at(e);
    e.currentTarget.setPointerCapture(e.pointerId);
  }
  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const ctx = canvasRef.current!.getContext("2d")!;
    const p = at(e);
    ctx.beginPath();
    ctx.moveTo(last.current!.x, last.current!.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  }
  function end() {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current!.toDataURL("image/png"));
  }
  function clear() {
    const c = canvasRef.current!;
    c.getContext("2d")!.clearRect(0, 0, c.width, c.height);
    onChange("");
  }

  return (
    <div className="fawkes-inboeken__signature">
      <canvas
        ref={canvasRef}
        className="fawkes-inboeken__signature-canvas"
        data-placeholder={value ? undefined : placeholder}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
      />
      <button type="button" className="fawkes-inboeken__link" onClick={clear}>
        {retryLabel}
      </button>
    </div>
  );
}
